import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import { db } from '../firebase';
import { ref as dbRef, set, runTransaction } from "firebase/database";
import { translateToMarathi } from '../utils/translationUtils';

const OpenApplication = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        companyName: '',
        companyAddress: '',
        personName: '',
        mobileNumber: '',
        nationality: '',
        permanentAddress: '',
        registeredAddress: '',
        headOfficeAddress: '',
        panNo: '',
        classSelection: 'ब',
        workEligibility: '15 लक्ष',
        workLocation: '',
        bankName: '',
        bankAddress: ''
    });

    const [uploading, setUploading] = useState(false);
    const translationTimeoutRef = React.useRef(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Trigger translation ONLY for specific fields
        const translationFields = ['companyName', 'companyAddress', 'personName', 'permanentAddress', 'registeredAddress', 'headOfficeAddress', 'bankName', 'bankAddress'];
        if (translationFields.includes(name)) {
            if (translationTimeoutRef.current) {
                clearTimeout(translationTimeoutRef.current);
            }

            const performTranslation = async (textValue) => {
                if (textValue.trim()) {
                    const translated = await translateToMarathi(textValue);
                    setFormData(prev => ({
                        ...prev,
                        [name]: translated // Replace original English with Marathi
                    }));
                }
            };

            // If the last character is a space, translate immediately
            if (value.endsWith(' ')) {
                performTranslation(value);
            } else {
                // Otherwise, use debounce
                translationTimeoutRef.current = setTimeout(() => {
                    performTranslation(value);
                }, 700);
            }
        }
    };

    const handleReset = () => {
        setFormData({
            companyName: '',
            companyAddress: '',
            personName: '',
            mobileNumber: '',
            nationality: '',
            permanentAddress: '',
            registeredAddress: '',
            headOfficeAddress: '',
            panNo: '',
            classSelection: 'ब',
            workEligibility: '15 लक्ष',
            workLocation: '',
            bankName: '',
            bankAddress: ''
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setUploading(true);

        try {
            // 1. Generate incremental counter for OpenApplication
            const counterRef = dbRef(db, 'ekamwatapzp/Counters/OpenApplicationCount');
            const result = await runTransaction(counterRef, (currentCount) => {
                return (currentCount || 0) + 1;
            });

            const newCount = result.snapshot.val();
            const applicationId = String(newCount).padStart(4, '0');

            // 2. Combine form data with metadata and default role
            const finalData = {
                ...formData,
                applicationId: applicationId,
                role: 'open',
                status: 'pending',
                submittedAt: new Date().toISOString()
            };

            // 3. Save to Firebase using mobileNumber as the key for uniqueness
            const applicationRef = dbRef(db, `ekamwatapzp/OpenApplications/${formData.mobileNumber || applicationId}`);
            await set(applicationRef, finalData);
            
            alert("अर्ज यशस्वीरित्या सादर केला!");
            handleReset();
            navigate(-1);
        } catch (error) {
            alert("त्रुटी: " + error.message);
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            <Navbar />
            
            <main className="flex-grow py-12 px-4">
                <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-xl border border-yellow-200 overflow-hidden">
                    {/* Header Section */}
                    <div className="bg-gradient-to-r from-blue-50 to-white p-8 border-b border-gray-100 text-center">
                        <h1 className="text-3xl font-extrabold text-blue-900 mb-2">महाराष्ट्र शासन</h1>
                        <h2 className="text-2xl font-bold text-blue-800 mb-4">सांगली जिल्हा परिषद</h2>
                        <div className="inline-block px-6 py-2 bg-blue-100 text-blue-900 rounded-full font-bold text-lg">
                            खुला अर्ज नोंदणी फॉर्म
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="p-8 md:p-12 space-y-10">
                        {/* Company Details Section */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <FormInput 
                                label="कंपनीचे नाव" 
                                name="companyName" 
                                placeholder="कंपनीचे नाव प्रविष्ट करा" 
                                required 
                                value={formData.companyName}
                                onChange={handleChange}
                            />
                            <FormInput 
                                label="कंपनीचा संपूर्ण पत्ता" 
                                name="companyAddress" 
                                placeholder="पत्ता प्रविष्ट करा" 
                                required 
                                value={formData.companyAddress}
                                onChange={handleChange}
                            />
                            <FormInput 
                                label="व्यक्तीचे नाव" 
                                name="personName" 
                                placeholder="कृपया नाव प्रविष्ट करा" 
                                required 
                                value={formData.personName}
                                onChange={handleChange}
                            />
                            
                            <FormInput 
                                label="मोबाईल नंबर" 
                                name="mobileNumber" 
                                placeholder="मोबाईल नंबर प्रविष्ट करा" 
                                required 
                                value={formData.mobileNumber}
                                onChange={handleChange}
                            />
                            <FormInput 
                                label="राष्ट्रीयत्व" 
                                name="nationality" 
                                placeholder="राष्ट्रीयत्व प्रविष्ट करा" 
                                required 
                                value={formData.nationality}
                                onChange={handleChange}
                            />
                            <FormInput 
                                label="कायमचा पत्ता" 
                                name="permanentAddress" 
                                placeholder="कायमचा पत्ता प्रविष्ट करा" 
                                required 
                                value={formData.permanentAddress}
                                onChange={handleChange}
                            />

                            <FormInput 
                                label="नोंदणीकृत पत्ता" 
                                name="registeredAddress" 
                                placeholder="नोंदणीकृत पत्ता प्रविष्ट करा" 
                                value={formData.registeredAddress}
                                onChange={handleChange}
                            />
                            <FormInput 
                                label="मुख्य कार्यालयाचा पत्ता" 
                                name="headOfficeAddress" 
                                placeholder="मुख्य कार्यालयाचा पत्ता प्रविष्ट करा" 
                                value={formData.headOfficeAddress}
                                onChange={handleChange}
                            />
                            <FormInput 
                                label="पॅन क्रमांक" 
                                name="panNo" 
                                placeholder="पॅन क्रमांक प्रविष्ट करा" 
                                required 
                                value={formData.panNo}
                                onChange={handleChange}
                            />

                            <FormSelect 
                                label="वर्ग निवडा" 
                                name="classSelection" 
                                options={[
                                    { value: 'अ', label: 'अ' },
                                    { value: 'ब', label: 'ब' },
                                    { value: 'क', label: 'क' },
                                    { value: 'ड', label: 'ड' }
                                ]} 
                                required 
                                value={formData.classSelection}
                                onChange={handleChange}
                            />
                            <FormSelect 
                                label="कामे करण्याची पात्रता" 
                                name="workEligibility" 
                                options={[
                                    { value: '10 लक्ष', label: '10 लक्ष' },
                                    { value: '15 लक्ष', label: '15 लक्ष' },
                                    { value: '25 लक्ष', label: '25 लक्ष' },
                                    { value: '50 लक्ष', label: '50 लक्ष' },
                                    { value: 'अमर्यादित', label: 'अमर्यादित' }
                                ]} 
                                required 
                                value={formData.workEligibility}
                                onChange={handleChange}
                            />
                            <FormInput 
                                label="कामाचे ठिकाण" 
                                name="workLocation" 
                                placeholder="कामाचे ठिकाण प्रविष्ट करा" 
                                required 
                                value={formData.workLocation}
                                onChange={handleChange}
                            />
                            
                            <FormInput 
                                label="बँकेचे नाव" 
                                name="bankName" 
                                placeholder="बँकेचे नाव प्रविष्ट करा" 
                                required 
                                value={formData.bankName}
                                onChange={handleChange}
                            />
                        </div>
                        
                        <div className="w-full">
                            <FormInput 
                                label="बँकेचा पत्ता" 
                                name="bankAddress" 
                                placeholder="बँकेचा पत्ता प्रविष्ट करा" 
                                fullWidth
                                value={formData.bankAddress}
                                onChange={handleChange}
                            />
                        </div>

                        {/* Submit Actions */}
                        <div className="flex justify-end space-x-4 pt-8 border-t border-gray-100">
                           <button 
                              type="submit" 
                              disabled={uploading}
                              className={`${uploading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-900 hover:bg-blue-800'} text-white px-10 py-3 rounded-lg font-bold shadow-lg transition transform hover:scale-105`}
                           >
                              {uploading ? 'Uploading...' : 'Submit'}
                           </button>
                           <button 
                              type="button" 
                              onClick={handleReset}
                              className="bg-gray-500 hover:bg-gray-600 text-white px-10 py-3 rounded-lg font-bold shadow-lg transition transform hover:scale-105"
                           >
                              Reset
                           </button>
                        </div>
                    </form>
                </div>
            </main>
            <Footer />
        </div>
    );
};

// Form Helper Components matching EngineerRegistration.js
const FormInput = ({ label, name, placeholder, type = "text", required = false, fullWidth = false, value, onChange }) => (
    <div className={fullWidth ? "w-full" : ""}>
        <label className="block text-gray-700 font-bold mb-2">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        <input 
            type={type} 
            name={name}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
            placeholder={placeholder}
            required={required}
            value={value}
            onChange={onChange}
        />
    </div>
);

const FormSelect = ({ label, name, options, required = false, value, onChange }) => (
    <div>
        <label className="block text-gray-700 font-bold mb-2">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        <select 
            name={name}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white"
            required={required}
            value={value}
            onChange={onChange}
        >
            <option value="">निवडा</option>
            {options.map((opt, idx) => (
                <option key={idx} value={opt.value}>{opt.label}</option>
            ))}
        </select>
    </div>
);

export default OpenApplication;
